/**
 * The identity cookies of the analytics (INST §03): who this browser is, across visits and for the
 * length of one visit, and which list brought the person here.
 *
 * ```
 * anonymous_id          UUID, 12 months. Issued on the first visit after the accept.
 * first_touch_list_id   the id (UUID, never the slug) of the first list visited. Written once.
 * session_id            UUID, 30 minutes of inactivity. Renewed on every activity.
 * ```
 *
 * **Only after "Aceitar" (D-06).** With a refusal, or no answer, none of them is created, and a
 * refusal that follows an accept deletes all three. It is the same standard the Clarity follows and
 * the notice promises ("Usamos cookies para entender como o site é usado…"): creating them after a
 * "Recusar" would contradict the button. Each function that writes checks the stored answer itself,
 * so no caller can create one by forgetting to.
 *
 * Cookies and not `localStorage` because the server has to read them: the BFF forwards the first two
 * as headers on the three calls that identify a person (`identity-headers.ts`).
 *
 * This file is pure data and rules; the browser side is `identity-cookies-client.ts`.
 */

export const ANONYMOUS_ID_COOKIE = "anonymous_id";
export const FIRST_TOUCH_LIST_COOKIE = "first_touch_list_id";
export const SESSION_ID_COOKIE = "session_id";

export const IDENTITY_COOKIE_NAMES = [ANONYMOUS_ID_COOKIE, FIRST_TOUCH_LIST_COOKIE, SESSION_ID_COOKIE] as const;

/** Twelve months, from the day it is issued. */
export const ANONYMOUS_ID_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
/** Thirty minutes without activity, as the INST defines a session. */
export const SESSION_IDLE_SECONDS = 30 * 60;

/** The headers the BFF adds to the backend calls that identify a person (D-05). */
export const ANONYMOUS_ID_HEADER = "X-Anonymous-Id";
export const FIRST_TOUCH_LIST_ID_HEADER = "X-First-Touch-List-Id";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A cookie is user-controlled text. Only a UUID is accepted anywhere an id is used, so a forged
 * value can neither reach the dataLayer nor ride into a header.
 */
export function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

export type IdentityCookies = {
  anonymousId?: string;
  firstTouchListId?: string;
  sessionId?: string;
};

/** Reads the three out of a `Cookie` header or `document.cookie` string; a malformed one is absent. */
export function parseIdentityCookies(cookieString: string): IdentityCookies {
  const found: Record<string, string> = {};

  for (const part of cookieString.split(";")) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;

    const name = part.slice(0, separator).trim();
    if (!(IDENTITY_COOKIE_NAMES as readonly string[]).includes(name)) continue;

    found[name] = part.slice(separator + 1).trim();
  }

  return {
    ...(isUuid(found[ANONYMOUS_ID_COOKIE]) ? { anonymousId: found[ANONYMOUS_ID_COOKIE] } : {}),
    ...(isUuid(found[FIRST_TOUCH_LIST_COOKIE]) ? { firstTouchListId: found[FIRST_TOUCH_LIST_COOKIE] } : {}),
    ...(isUuid(found[SESSION_ID_COOKIE]) ? { sessionId: found[SESSION_ID_COOKIE] } : {}),
  };
}

/** The headers for the backend, only for what exists; nothing at all when there are no cookies. */
export function identityHeadersFor(identity: IdentityCookies): Record<string, string> {
  return {
    ...(identity.anonymousId ? { [ANONYMOUS_ID_HEADER]: identity.anonymousId } : {}),
    ...(identity.firstTouchListId ? { [FIRST_TOUCH_LIST_ID_HEADER]: identity.firstTouchListId } : {}),
  };
}
